import { action, makeAutoObservable, observable, runInAction } from 'mobx';

import { ContentListProviders } from '@smart-tv/typings/content';
import { EpisodeExtended } from '@smart-tv/components/program-sheet/types';
import getContentInfoEpisodes from '@smart-tv/utils/kmm/getContentInfoEpisodes';
import getNextEventForContentId from '@smart-tv/utils/kmm/getNextEventForContentId';

import { RootStore } from './store-root';

// Fetched once, up front, in a single request - the episode list needs the complete array
// client-side (season tabs will act as anchors into it), so this isn't real server-side
// pagination; the sentinel in the list component only delays rendering, not fetching.
export const ALL_EPISODES_LIMIT = 999;

interface SeasonEpisodesParams {
  providers?: ContentListProviders;
  seasonIds?: number[];
  start: number;
  limit: number;
  contentId: number;
  contentType?: string;
  universeId?: number;
  providerCollectionId?: number;
  subProviderId?: string;
}

class EpisodesStore {
  rootStore: RootStore;
  seasons = [] as any;
  fetchedEpisodes = {} as any;
  isFetchingEpisodes = false;
  nextEventForSeries = {} as EpisodeExtended;
  focusedEpisode = {
    episodeId: 0,
    isNextEventforSeries: false,
    episodeTitle: ''
  } as { episodeId: number; isNextEventforSeries: boolean; episodeTitle: string };
  episodesIds = [] as number[];
  fetchGeneration = 0;
  activeRequestId = 0;
  lastEpisodeListNavigationKey: string | null = null;

  constructor(root: RootStore) {
    this.rootStore = root;
    makeAutoObservable(this, {
      fetchEpisodes: action.bound,
      setFocusedEpisode: action.bound,
      fetchedEpisodes: observable,
      resetFetchedEpisodes: action.bound,
      setNextEventForSeries: action.bound,
      setEpisodesIds: action.bound,
      setLastEpisodeListNavigationKey: action.bound,
      cancelPendingFetch: action.bound
    });
  }

  // The backend rejects episode requests while a stream is active so we "cancel" any pending fetches when playback starts.
  cancelPendingFetch = () => {
    if (!this.isFetchingEpisodes) return;
    this.activeRequestId++;
    this.isFetchingEpisodes = false;
  };

  setLastEpisodeListNavigationKey = (key: string | null) => {
    this.lastEpisodeListNavigationKey = key;
  };

  get episodes() {
    const mergeEpisodes = [] as EpisodeExtended[];

    if (
      this.nextEventForSeries.episodeId &&
      this.fetchedEpisodes &&
      this.fetchedEpisodes.episodes &&
      !this.rootStore.seasonsStore.seasonFilterActive
    ) {
      mergeEpisodes.push(this.nextEventForSeries, ...this.fetchedEpisodes.episodes);
    } else if (this.fetchedEpisodes && this.fetchedEpisodes.episodes) {
      mergeEpisodes.push(...this.fetchedEpisodes.episodes);
    }

    return mergeEpisodes;
  }

  get episodesCount() {
    const totalEpisodes =
      this.fetchedEpisodes && typeof this.fetchedEpisodes.totalEpisodes === 'number'
        ? this.fetchedEpisodes.totalEpisodes
        : 0;
    return totalEpisodes;
  }

  setEpisodesIds = (ids: number[]) => {
    this.episodesIds = ids;
  };

  setNextEventForSeries(payload: any) {
    this.nextEventForSeries = payload;
  }

  resetFetchedEpisodes = (clearUpcoming = true, clearEpisodes = true) => {
    if (clearEpisodes) {
      this.fetchedEpisodes = {} as any;
    }
    if (clearUpcoming) {
      this.nextEventForSeries = {} as EpisodeExtended;
    }
  };

  setFocusedEpisode = (episodeId: number, isNextEventforSeries: boolean, episodeTitle: string) => {
    this.focusedEpisode = {
      episodeId: episodeId,
      isNextEventforSeries,
      episodeTitle: episodeTitle
    };
  };

  fetchEpisodes = async (props: SeasonEpisodesParams) => {
    const {
      seasonIds,
      start,
      limit,
      contentId,
      contentType,
      universeId,
      providerCollectionId,
      subProviderId
    } = props;

    if (this.rootStore.sheetStore.isPlayerOpen) return;

    const requestId = ++this.activeRequestId;
    const isStale = () => requestId !== this.activeRequestId;

    this.isFetchingEpisodes = true;

    //this will happen fx when choosing a new season
    // Keep the currently rendered episodes/detail in place until the new ones arrive,
    // so the list doesn't briefly collapse and cause the sheet to jump-scroll.
    if (start === 0) {
      this.resetFetchedEpisodes(!this.rootStore.seasonsStore.seasonFilterActive, false);
    }

    if (seasonIds && seasonIds.length) {
      this.setEpisodesIds(seasonIds);
    }

    try {
      if (start === 0 && !this.rootStore.seasonsStore.seasonFilterActive) {
        try {
          const nextEvent = await getNextEventForContentId({
            contentId,
            contentType,
            context: {
              ...(universeId !== undefined ? { universeId } : {}),
              ...(providerCollectionId !== undefined ? { providerCollectionId } : {}),
              ...(subProviderId ? { subProviderId } : {})
            }
          });

          if (isStale()) return;
          runInAction(() => this.setNextEventForSeries(nextEvent ?? ({} as EpisodeExtended)));
        } catch {
          if (isStale()) return;
          runInAction(() => this.setNextEventForSeries({} as EpisodeExtended));
        }
      }

      const response = await getContentInfoEpisodes(
        seasonIds ?? [],
        start,
        limit,
        contentId,
        universeId ?? undefined,
        providerCollectionId ?? undefined,
        subProviderId,
        ContentListProviders.All
      );
      if (isStale()) return;
      runInAction(() => {
        if (start > 0 && this.fetchedEpisodes.episodes) {
          // Append new episodes to existing ones
          this.fetchedEpisodes = {
            ...response,
            episodes: [...this.fetchedEpisodes.episodes, ...response.episodes]
          };
        } else {
          // Initial load
          this.fetchedEpisodes = response;
        }
      });
    } catch {
      if (isStale()) return;
      runInAction(() => {
        if (start === 0) {
          this.fetchedEpisodes = { episodes: [], totalEpisodes: 0 };
        }
        this.nextEventForSeries = {} as EpisodeExtended;
      });
    } finally {
      if (!isStale()) {
        runInAction(() => {
          this.isFetchingEpisodes = false;
          this.fetchGeneration++;
        });
      }
    }
  };
}

export default EpisodesStore;
